import { FC } from 'react'
import { DepositTokenLink } from '../../../../../../common/DepositTokenLink'
import { formatSum } from '../../../../../../../utils'
import { LIABLE_OPTIONS, REMEDY_OPTIONS } from '../../core/consts'
import { AMOUNT_INPUT_PATTERN } from '../../../../../../common/core/amountInput'
import { CulpritDraft, openShortfall, shortfallState } from '../../core/culpritFacts'
import { CulpritCandidate } from '../../core/response.types'

interface Props {
  candidate: CulpritCandidate
  draft: CulpritDraft
  onChange: (draft: CulpritDraft) => void
  disabled?: boolean
}

// One culprit deposit of a payout dispute: what reached the client and, when something is missing,
// who answers for it and how the client is made whole. The operation itself is derived on the server.
export const PayoutCulpritRemedyBlock: FC<Props> = ({ candidate, draft, onChange, disabled }) => {
  const state = shortfallState(candidate, draft)
  const id = `culprit-paid-${candidate.token}`

  return (
    <div className='border rounded p-3 mb-3'>
      <div className='d-flex justify-content-between mb-2'>
        <DepositTokenLink token={candidate.token} />
        <span className='text-muted small'>
          должно было дойти {formatSum(candidate.body)} {candidate.currency}
          {Number(candidate.remedied) > 0 && <> · уже исправлено {formatSum(candidate.remedied)}</>}
        </span>
      </div>
      <label className='form-label required' htmlFor={id}>
        Сколько дошло получателю
      </label>
      <input
        id={id}
        type='text'
        inputMode='decimal'
        className='form-control mb-2'
        autoComplete='off'
        value={draft.paid}
        disabled={disabled}
        onChange={(e) => {
          if (AMOUNT_INPUT_PATTERN.test(e.target.value))
            onChange({ ...draft, paid: e.target.value })
        }}
      />
      {state === 'none' && (
        <div className='text-muted small'>По этому депозиту проводить нечего</div>
      )}
      {state === 'arrivedLater' && (
        <div className='text-muted small'>
          Деньги дошли после исправления — будет проведена обратная операция
        </div>
      )}
      {state === 'missing' && (
        <>
          <div className='small mb-2'>
            Недостача {formatSum(openShortfall(candidate, draft))} {candidate.currency}
          </div>
          <fieldset className='mb-2'>
            <legend className='form-label fs-7 required'>Кто отвечает за недостачу</legend>
            {LIABLE_OPTIONS.map((o) => (
              <div key={o.value} className='form-check form-check-custom form-check-sm mb-1'>
                <input
                  type='radio'
                  className='form-check-input'
                  id={`${id}-liable-${o.value}`}
                  checked={draft.liable === o.value}
                  disabled={disabled}
                  onChange={() => onChange({ ...draft, liable: o.value })}
                />
                <label className='form-check-label' htmlFor={`${id}-liable-${o.value}`}>
                  {o.label}
                </label>
              </div>
            ))}
          </fieldset>
          <fieldset>
            <legend className='form-label fs-7 required'>Как получатель получит недостающее</legend>
            {REMEDY_OPTIONS.map((o) => (
              <div key={o.value} className='form-check form-check-custom form-check-sm mb-1'>
                <input
                  type='radio'
                  className='form-check-input'
                  id={`${id}-remedy-${o.value}`}
                  checked={draft.remedy === o.value}
                  disabled={disabled}
                  onChange={() => onChange({ ...draft, remedy: o.value })}
                />
                <label className='form-check-label' htmlFor={`${id}-remedy-${o.value}`}>
                  {o.label}
                </label>
              </div>
            ))}
          </fieldset>
        </>
      )}
    </div>
  )
}

export default PayoutCulpritRemedyBlock
